import React, { useState, useEffect } from 'react';

// For MVP, we'll use SVG graphics for the pet with enhanced animations
const PetDisplay = ({ state = 'good' }) => {
  const [animationFrame, setAnimationFrame] = useState(0);
  
  // Set up animation loop
  useEffect(() => {
    const animation = getAnimation(state);
    let animationInterval;
    
    if (animation.frames > 1) {
      animationInterval = setInterval(() => {
        setAnimationFrame(prev => (prev + 1) % animation.frames);
      }, animation.interval);
    }
    
    return () => {
      if (animationInterval) clearInterval(animationInterval);
    };
  }, [state]);

  return (
    <div className="pet-container flex flex-col items-center justify-center p-4">
      <div className={`relative w-64 h-64 ${getEnvironmentClass(state)}`}>
        <svg 
          viewBox="0 0 200 200" 
          className={`w-full h-full transition-all duration-500 ${getStateClass(state)}`}
          style={{ imageRendering: 'pixelated' }}
        >
          {/* Environment elements based on state */}
          {getEnvironment(state, animationFrame)}
          
          {/* Pet body */}
          <g className="pet-body">
            <rect 
              x="70" 
              y="70" 
              width="60" 
              height="60" 
              className={`fill-primary-500 transition-all duration-300 ${getBodyClass(state)}`} 
              rx="5"
              ry="5"
            />
            
            {/* Antenna */}
            <rect x="95" y="50" width="10" height="20" className="fill-primary-600" />
            <circle cx="100" cy="45" r="8" className="fill-accent-400" />
          </g>
          
          {/* Eyes */}
          {getEyes(state, animationFrame)}
          
          {/* Mouth */}
          {getMouth(state, animationFrame)}
          
          {/* State-specific decorations */}
          {getDecorations(state, animationFrame)}
        </svg>
      </div>
      <div className="text-center mt-4 font-pixel text-lg text-primary-400">{getStateName(state)}</div>
    </div>
  );
};

// Helper function to get animation properties
function getAnimation(state) {
  const animations = {
    'starving': { frames: 2, interval: 500 },
    'exhausted': { frames: 2, interval: 1200 },
    'depressed': { frames: 2, interval: 1000 },
    'hungry': { frames: 2, interval: 600 },
    'tired': { frames: 2, interval: 800 },
    'sad': { frames: 2, interval: 700 },
    'excellent': { frames: 4, interval: 300 },
    'good': { frames: 2, interval: 500 },
    'fair': { frames: 2, interval: 600 },
    'poor': { frames: 2, interval: 700 },
    'disconnected': { frames: 2, interval: 800 }
  };
  
  return animations[state] || { frames: 1, interval: 1000 };
}

// Helper functions to generate SVG elements based on state
function getStateClass(state) {
  const classes = {
    'starving': 'animate-pulse',
    'exhausted': 'opacity-70',
    'depressed': 'opacity-80',
    'hungry': 'animate-pulse',
    'tired': 'opacity-90',
    'sad': 'opacity-90',
    'excellent': '',
    'good': '',
    'fair': '',
    'poor': 'opacity-80',
    'disconnected': 'opacity-60'
  };
  
  return classes[state] || '';
}

function getBodyClass(state) {
  const classes = {
    'starving': 'scale-95',
    'exhausted': 'scale-90',
    'depressed': 'scale-95 translate-y-2',
    'hungry': 'scale-98',
    'tired': 'scale-95',
    'sad': 'scale-98',
    'excellent': 'scale-100',
    'good': 'scale-100',
    'fair': 'scale-98',
    'poor': 'scale-95',
    'disconnected': 'scale-90 translate-y-2'
  };
  
  return classes[state] || 'scale-100';
}

function getEnvironmentClass(state) {
  const classes = {
    'starving': 'bg-dark-700',
    'exhausted': 'bg-dark-800',
    'depressed': 'bg-dark-900',
    'hungry': 'bg-dark-700',
    'tired': 'bg-dark-800',
    'sad': 'bg-dark-900',
    'excellent': 'bg-dark-700',
    'good': 'bg-dark-700',
    'fair': 'bg-dark-800',
    'poor': 'bg-dark-900',
    'disconnected': 'bg-dark-950'
  };
  
  return classes[state] || 'bg-dark-800';
}

function getEnvironment(state, frame) {
  switch (state) {
    case 'depressed':
    case 'sad':
      // Rain clouds - pixel style
      return (
        <>
          <rect x="40" y="20" width="10" height="10" fill="#606060" />
          <rect x="50" y="20" width="10" height="10" fill="#606060" />
          <rect x="60" y="20" width="10" height="10" fill="#606060" />
          <rect x="70" y="20" width="10" height="10" fill="#606060" />
          <rect x="80" y="20" width="10" height="10" fill="#606060" />
          <rect x={45 + frame * 5} y={35 + frame * 5} width="5" height="10" fill="#4080ff" />
          <rect x={65 + frame * 5} y={35 + frame * 5} width="5" height="10" fill="#4080ff" />
          <rect x={85 + frame * 5} y={35 + frame * 5} width="5" height="10" fill="#4080ff" />
        </>
      );
    case 'disconnected':
      // Broken connection symbols - pixel style
      return (
        <>
          <rect x="30" y="30" width="5" height="5" fill="#888" />
          <rect x="35" y="35" width="5" height="5" fill="#888" />
          <rect x="40" y="40" width="5" height="5" fill="#888" />
          <rect x="45" y="45" width="5" height="5" fill="#888" />
          
          <rect x="30" y="45" width="5" height="5" fill="#888" />
          <rect x="35" y="40" width="5" height="5" fill="#888" />
          <rect x="40" y="35" width="5" height="5" fill="#888" />
          <rect x="45" y="30" width="5" height="5" fill="#888" />
          
          <rect x={150 + frame * 5} y="30" width="5" height="5" fill="#888" />
          <rect x={145 + frame * 5} y="35" width="5" height="5" fill="#888" />
          <rect x={140 + frame * 5} y="40" width="5" height="5" fill="#888" />
          <rect x={135 + frame * 5} y="45" width="5" height="5" fill="#888" />
        </>
      );
    case 'excellent':
      // Same as good state but with star shapes instead of squares
      return (
        <>
          <rect x={50 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={60 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={70 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={55 + frame * 2} y="25" width="10" height="5" fill="#505050" />
          <rect x={65 + frame * 2} y="25" width="10" height="5" fill="#505050" />
          
          {/* Star shapes instead of squares - fewer at top */}
          <polygon 
            points={`${30 + (frame % 4) * 5},${40} ${32 + (frame % 4) * 5},${35} ${35 + (frame % 4) * 5},${35} ${33 + (frame % 4) * 5},${32} ${35 + (frame % 4) * 5},${28} ${30 + (frame % 4) * 5},${30} ${25 + (frame % 4) * 5},${28} ${27 + (frame % 4) * 5},${32} ${25 + (frame % 4) * 5},${35} ${28 + (frame % 4) * 5},${35}`}
            fill="#FFD700"
            opacity={0.7 + (frame % 4) * 0.1}
          />
        </>
      );
    case 'good':
      // Light clouds - pixel style
      return (
        <>
          <rect x={50 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={60 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={70 + frame * 2} y="30" width="10" height="5" fill="#505050" />
          <rect x={55 + frame * 2} y="25" width="10" height="5" fill="#505050" />
          <rect x={65 + frame * 2} y="25" width="10" height="5" fill="#505050" />
        </>
      );
    default:
      return null;
  }
}

function getEyes(state, frame) {
  switch (state) {
    case 'disconnected':
      // Offline eyes (X X) - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          <rect x="80" y="80" width="2" height="2" fill="#444" />
          <rect x="82" y="82" width="2" height="2" fill="#444" />
          <rect x="84" y="84" width="2" height="2" fill="#444" />
          <rect x="86" y="86" width="2" height="2" fill="#444" />
          <rect x="88" y="88" width="2" height="2" fill="#444" />
          
          <rect x="88" y="80" width="2" height="2" fill="#444" />
          <rect x="86" y="82" width="2" height="2" fill="#444" />
          <rect x="84" y="84" width="2" height="2" fill="#444" />
          <rect x="82" y="86" width="2" height="2" fill="#444" />
          <rect x="80" y="88" width="2" height="2" fill="#444" />
          
          <rect x="110" y="80" width="2" height="2" fill="#444" />
          <rect x="112" y="82" width="2" height="2" fill="#444" />
          <rect x="114" y="84" width="2" height="2" fill="#444" />
          <rect x="116" y="86" width="2" height="2" fill="#444" />
          <rect x="118" y="88" width="2" height="2" fill="#444" />
          
          <rect x="118" y="80" width="2" height="2" fill="#444" />
          <rect x="116" y="82" width="2" height="2" fill="#444" />
          <rect x="114" y="84" width="2" height="2" fill="#444" />
          <rect x="112" y="86" width="2" height="2" fill="#444" />
          <rect x="110" y="88" width="2" height="2" fill="#444" />
        </>
      );
    case 'starving':
      // Dizzy, spiral eyes - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          <rect x="83" y="83" width="4" height="4" fill="#444" className={frame === 0 ? "animate-spin-slow" : ""} />
          <rect x="113" y="83" width="4" height="4" fill="#444" className={frame === 0 ? "animate-spin-slow" : ""} />
        </>
      );
    case 'exhausted':
    case 'depressed':
      // Closed, sad eyes - pixel style
      return (
        <>
          <rect x="75" y="85" width="20" height="2" fill="white" />
          <rect x="105" y="85" width="20" height="2" fill="white" />
          {frame === 1 && (
            <>
              <rect x="70" y="80" width="5" height="2" fill="white" />
              <rect x="100" y="80" width="5" height="2" fill="white" />
            </>
          )}
        </>
      );
    case 'tired':
      // Half-closed eyes - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height={frame === 0 ? 6 : 4} fill="white" />
          <rect x="110" y="80" width="10" height={frame === 0 ? 6 : 4} fill="white" />
        </>
      );
    case 'sad':
      // Teary eyes - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          <rect x="83" y="83" width="4" height="4" fill="#444" />
          <rect x="113" y="83" width="4" height="4" fill="#444" />
          {frame === 1 && (
            <>
              <rect x="85" y="90" width="3" height="3" fill="#4080ff" />
              <rect x="85" y="93" width="3" height="3" fill="#4080ff" />
              <rect x="85" y="96" width="3" height="3" fill="#4080ff" />
              
              <rect x="115" y="90" width="3" height="3" fill="#4080ff" />
              <rect x="115" y="93" width="3" height="3" fill="#4080ff" />
              <rect x="115" y="96" width="3" height="3" fill="#4080ff" />
            </>
          )}
        </>
      );
    case 'excellent':
      // Eyes with pulsing heart pupils
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          
          {/* Left eye heart - pulsing without moving */}
          <g transform={`translate(85, 85) scale(${1 + (frame % 4) * 0.1}) translate(-85, -85)`}>
            <rect x="82" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="84" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="86" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="82" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="84" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="86" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="83" y="87" width="2" height="2" fill="#FF69B4" />
            <rect x="85" y="87" width="2" height="2" fill="#FF69B4" />
            <rect x="84" y="89" width="2" height="1" fill="#FF69B4" />
          </g>
          
          {/* Right eye heart - pulsing without moving */}
          <g transform={`translate(115, 85) scale(${1 + (frame % 4) * 0.1}) translate(-115, -85)`}>
            <rect x="112" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="114" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="116" y="83" width="2" height="2" fill="#FF69B4" />
            <rect x="112" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="114" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="116" y="85" width="2" height="2" fill="#FF69B4" />
            <rect x="113" y="87" width="2" height="2" fill="#FF69B4" />
            <rect x="115" y="87" width="2" height="2" fill="#FF69B4" />
            <rect x="114" y="89" width="2" height="1" fill="#FF69B4" />
          </g>
        </>
      );
    case 'hungry':
      // Hungry eyes - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          <rect x="83" y="83" width="4" height="4" fill="#444" />
          <rect x="113" y="83" width="4" height="4" fill="#444" />
          {frame === 1 && (
            <>
              <rect x="90" y="85" width="5" height="2" fill="#ff8080" />
              <rect x="120" y="85" width="5" height="2" fill="#ff8080" />
            </>
          )}
        </>
      );
    default:
      // Default eyes - pixel style
      return (
        <>
          <rect x="80" y="80" width="10" height="10" fill="white" />
          <rect x="110" y="80" width="10" height="10" fill="white" />
          <rect x="83" y="83" width="4" height="4" fill="#444" />
          <rect x="113" y="83" width="4" height="4" fill="#444" />
        </>
      );
  }
}

function getMouth(state, frame) {
  switch (state) {
    case 'excellent':
      // Same as good state
      return (
        <path 
          d="M80,95 Q100,110 120,95" 
          stroke="#333" 
          strokeWidth="3" 
          fill="none"
        />
      );
    case 'good':
      // Happy mouth
      return (
        <path 
          d="M80,95 Q100,110 120,95" 
          stroke="#333" 
          strokeWidth="3" 
          fill="none"
        />
      );
    case 'fair':
      // Slight smile
      return (
        <path 
          d="M80,95 Q100,105 120,95" 
          stroke="#333" 
          strokeWidth="3" 
          fill="none"
        />
      );
    case 'sad':
    case 'depressed':
      // Sad mouth - inverted smile
      return (
        <path 
          d="M80,105 Q100,90 120,105" 
          stroke="#333" 
          strokeWidth="3" 
          fill="none"
        />
      );
    case 'hungry':
      // Open mouth
      return (
        <>
          <rect x="90" y="95" width="20" height="15" fill="#333" />
          {frame === 0 && <rect x="95" y="100" width="10" height="5" fill="#ff6b6b" />}
        </>
      );
    case 'tired':
      // Yawning mouth
      return (
        <ellipse 
          cx="100" 
          cy="100" 
          rx={frame === 0 ? "10" : "15"} 
          ry={frame === 0 ? "5" : "10"} 
          fill="#333" 
        />
      );
    case 'starving':
      // Desperate open mouth with animation
      return (
        <>
          <rect 
            x="85" 
            y={`${95 + (frame % 2 === 0 ? 0 : 2)}`} 
            width="30" 
            height={`${20 - (frame % 2 === 0 ? 0 : 2)}`} 
            fill="#333" 
          />
          <rect 
            x="90" 
            y={`${100 + (frame % 2 === 0 ? 0 : 1)}`} 
            width="20" 
            height={`${10 - (frame % 2 === 0 ? 0 : 1)}`} 
            fill="#ff6b6b" 
          />
        </>
      );
    case 'exhausted':
      // Flat line
      return (
        <rect 
          x="80" 
          y="100" 
          width="40" 
          height="3" 
          fill="#333" 
        />
      );
    case 'disconnected':
      // X mouth
      return (
        <>
          <rect x="90" y="95" width="20" height="3" fill="#555" transform="rotate(45, 100, 100)" />
          <rect x="90" y="95" width="20" height="3" fill="#555" transform="rotate(-45, 100, 100)" />
        </>
      );
    case 'poor':
      // Neutral mouth
      return (
        <rect 
          x="85" 
          y="100" 
          width="30" 
          height="3" 
          fill="#333" 
        />
      );
    default:
      // Default mouth
      return (
        <rect 
          x="85" 
          y="100" 
          width="30" 
          height="3" 
          fill="#333" 
        />
      );
  }
}

function getDecorations(state, frame) {
  switch (state) {
    case 'excellent':
      // Same as good state with star shapes instead of circles
      return (
        <>
          {/* Sun in the corner - exactly as in good state */}
          <circle cx="40" cy="40" r="15" fill="#FFD700" />
          <g>
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
              <line 
                key={i}
                x1={40 + Math.cos(angle * Math.PI / 180) * 15}
                y1={40 + Math.sin(angle * Math.PI / 180) * 15}
                x2={40 + Math.cos(angle * Math.PI / 180) * (20 + (frame * 2))}
                y2={40 + Math.sin(angle * Math.PI / 180) * (20 + (frame * 2))}
                stroke="#FFD700"
                strokeWidth="2"
              />
            ))}
          </g>
          
          {/* Star shapes instead of circles - more at bottom left */}
          <polygon 
            points={`${160 - frame * 5},${40} ${162 - frame * 5},${35} ${165 - frame * 5},${35} ${163 - frame * 5},${32} ${165 - frame * 5},${28} ${160 - frame * 5},${30} ${155 - frame * 5},${28} ${157 - frame * 5},${32} ${155 - frame * 5},${35} ${158 - frame * 5},${35}`}
            fill="#FFEC8B"
            opacity={0.7 + (frame % 4) * 0.1}
          />
          
          <polygon 
            points={`${150},${140 - frame * 3} ${152},${135 - frame * 3} ${155},${135 - frame * 3} ${153},${132 - frame * 3} ${155},${128 - frame * 3} ${150},${130 - frame * 3} ${145},${128 - frame * 3} ${147},${132 - frame * 3} ${145},${135 - frame * 3} ${148},${135 - frame * 3}`}
            fill="#FFEC8B"
            opacity={0.7 + (frame % 4) * 0.1}
          />
          
          {/* More stars at bottom left */}
          <polygon 
            points={`${50 + frame * 2},${150} ${52 + frame * 2},${145} ${55 + frame * 2},${145} ${53 + frame * 2},${142} ${55 + frame * 2},${138} ${50 + frame * 2},${140} ${45 + frame * 2},${138} ${47 + frame * 2},${142} ${45 + frame * 2},${145} ${48 + frame * 2},${145}`}
            fill="#FFEC8B"
            opacity={0.8 + (frame % 4) * 0.1}
          />
          
          <polygon 
            points={`${30 + frame * 3},${160} ${32 + frame * 3},${155} ${35 + frame * 3},${155} ${33 + frame * 3},${152} ${35 + frame * 3},${148} ${30 + frame * 3},${150} ${25 + frame * 3},${148} ${27 + frame * 3},${152} ${25 + frame * 3},${155} ${28 + frame * 3},${155}`}
            fill="#FFEC8B"
            opacity={0.8 + (frame % 4) * 0.1}
          />
          
          <polygon 
            points={`${70 + frame * 2},${170} ${72 + frame * 2},${165} ${75 + frame * 2},${165} ${73 + frame * 2},${162} ${75 + frame * 2},${158} ${70 + frame * 2},${160} ${65 + frame * 2},${158} ${67 + frame * 2},${162} ${65 + frame * 2},${165} ${68 + frame * 2},${165}`}
            fill="#FFEC8B"
            opacity={0.9 + (frame % 4) * 0.1}
          />
          
          <polygon 
            points={`${90 + frame * 3},${155} ${92 + frame * 3},${150} ${95 + frame * 3},${150} ${93 + frame * 3},${147} ${95 + frame * 3},${143} ${90 + frame * 3},${145} ${85 + frame * 3},${143} ${87 + frame * 3},${147} ${85 + frame * 3},${150} ${88 + frame * 3},${150}`}
            fill="#FFEC8B"
            opacity={0.8 + (frame % 4) * 0.1}
          />
          
          {/* A few more stars in other areas */}
          <polygon 
            points={`${130 - frame * 3},${70} ${132 - frame * 3},${65} ${135 - frame * 3},${65} ${133 - frame * 3},${62} ${135 - frame * 3},${58} ${130 - frame * 3},${60} ${125 - frame * 3},${58} ${127 - frame * 3},${62} ${125 - frame * 3},${65} ${128 - frame * 3},${65}`}
            fill="#FFEC8B"
            opacity={0.7 + (frame % 4) * 0.1}
          />
          
          <polygon 
            points={`${170 - frame * 4},${100} ${172 - frame * 4},${95} ${175 - frame * 4},${95} ${173 - frame * 4},${92} ${175 - frame * 4},${88} ${170 - frame * 4},${90} ${165 - frame * 4},${88} ${167 - frame * 4},${92} ${165 - frame * 4},${95} ${168 - frame * 4},${95}`}
            fill="#FFEC8B"
            opacity={0.8 + (frame % 4) * 0.1}
          />
        </>
      );
    case 'starving':
      // Improved starving state with stomach rumble animation and food icons
      return (
        <>
          {/* Stomach rumble animation */}
          <g className="animate-pulse">
            <path 
              d={`M${130 - frame * 2},${100 + frame} Q${135 - frame * 2},${105 + frame} ${130 - frame * 2},${110 + frame}`} 
              stroke="#333" 
              strokeWidth="1.5"
              fill="none"
            />
            <path 
              d={`M${135 - frame * 3},${105 + frame * 2} Q${140 - frame * 3},${110 + frame * 2} ${135 - frame * 3},${115 + frame * 2}`} 
              stroke="#333" 
              strokeWidth="1.5"
              fill="none"
            />
          </g>
          
          {/* Floating food icons around the pet */}
          <g className={frame % 2 === 0 ? "opacity-80" : "opacity-100"}>
            <circle cx="50" cy="60" r="8" fill="#D2691E" /> {/* Meatball */}
            <rect x="150" y="70" width="12" height="12" fill="#FFD700" /> {/* Cheese */}
            <circle cx="70" cy="150" r="6" fill="#FF6347" /> {/* Tomato */}
            <rect x="140" y="140" width="10" height="10" rx="2" fill="#8B4513" /> {/* Chocolate */}
          </g>
          
          {/* Hunger indicator */}
          <text 
            x="120" 
            y="50" 
            fontSize="12" 
            fontWeight="bold" 
            fill="#FF6347"
            className={frame % 2 === 0 ? "opacity-80" : "opacity-100"}
          >
            !
          </text>
          <text 
            x="130" 
            y="50" 
            fontSize="12" 
            fontWeight="bold" 
            fill="#FF6347"
            className={frame % 2 === 0 ? "opacity-100" : "opacity-80"}
          >
            !
          </text>
        </>
      );
    case 'good':
      // Sun and light effects for good state
      return (
        <>
          {/* Sun in the corner */}
          <circle cx="40" cy="40" r="15" fill="#FFD700" />
          <g>
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
              <line 
                key={i}
                x1={40 + Math.cos(angle * Math.PI / 180) * 15}
                y1={40 + Math.sin(angle * Math.PI / 180) * 15}
                x2={40 + Math.cos(angle * Math.PI / 180) * (20 + (frame * 2))}
                y2={40 + Math.sin(angle * Math.PI / 180) * (20 + (frame * 2))}
                stroke="#FFD700"
                strokeWidth="2"
              />
            ))}
          </g>
          
          {/* Light sparkles */}
          <circle cx={`${160 - frame * 5}`} cy="40" r={3 + frame} fill="#FFEC8B" opacity="0.7" />
          <circle cx="150" cy={`${140 - frame * 3}`} r={2 + frame} fill="#FFEC8B" opacity="0.7" />
        </>
      );
    case 'exhausted':
      // Z's for sleeping
      return (
        <>
          <text 
            x={`${130 + frame * 5}`} 
            y={`${50 - frame * 5}`} 
            fontSize="20" 
            fill="#6c757d" 
            fontWeight="bold"
          >
            Z
          </text>
          <text 
            x={`${145 + frame * 5}`} 
            y={`${40 - frame * 5}`} 
            fontSize="15" 
            fill="#6c757d" 
            fontWeight="bold"
          >
            Z
          </text>
          <text 
            x={`${155 + frame * 5}`} 
            y={`${30 - frame * 5}`} 
            fontSize="10" 
            fill="#6c757d" 
            fontWeight="bold"
          >
            Z
          </text>
        </>
      );
    case 'depressed':
      // Rain drops and dark cloud
      return (
        <>
          <rect x="130" y="40" width="30" height="10" fill="#555" rx="5" ry="5" />
          <rect x="125" y="35" width="40" height="10" fill="#444" rx="5" ry="5" />
          <rect x={`${130 + frame * 3}`} y={`${55 + frame * 5}`} width="3" height="10" fill="#4080ff" />
          <rect x={`${140 + frame * 5}`} y={`${60 + frame * 5}`} width="3" height="10" fill="#4080ff" />
          <rect x={`${150 + frame * 4}`} y={`${55 + frame * 5}`} width="3" height="10" fill="#4080ff" />
        </>
      );
    case 'sad':
      // Single tear drop
      return (
        <>
          <path 
            d={`M${85 + frame},${110 + frame * 5} Q${87 + frame},${115 + frame * 5} ${85 + frame},${120 + frame * 5}`} 
            fill="#4080ff" 
          />
        </>
      );
    default:
      return null;
  }
}

function getStateName(state) {
  const stateNames = {
    'starving': 'STARVING',
    'exhausted': 'EXHAUSTED',
    'depressed': 'DEPRESSED',
    'hungry': 'HUNGRY',
    'tired': 'TIRED',
    'sad': 'SAD',
    'excellent': 'EXCELLENT',
    'good': 'GOOD',
    'fair': 'FAIR',
    'poor': 'POOR',
    'disconnected': 'DISCONNECTED'
  };
  
  return stateNames[state] || 'UNKNOWN';
}

export default PetDisplay; 